import { useEffect, useMemo, useState } from "react";
import { Select, Button, DatePicker } from "antd";
import dayjs from "dayjs";
import { ReloadOutlined } from "@ant-design/icons";
import useSettingsDropdown from "../../../hooks/useSettingsDropdown";
import { MONTH_OPTIONS, YEAR_OPTIONS, ALL_VALUE } from "../constants";
import { fetchPartnerOptions } from "../dashboardData";

/**
 * DashboardFilterBar — Shared filter row for all dashboard tabs.
 * Project / Fleet / City / Zones from Settings; Partners from GET /riders.
 *
 * Two opt-in behaviours, currently used by the Overall tab only, so the other
 * tabs keep the single-select / clearable behaviour they were built against:
 *   showAllOption   — adds an explicit "All" entry to Project / Fleet / City
 *   multiZone       — Zones becomes multi-select (reads/writes `values.zones`)
 *                     and lists only the zones under the selected city
 *   allowAllPeriod  — adds "All" to Year / Month (Summary); Year = All forces
 *                     Month = All, since a month with no year is not a range
 *   singlePartner   — Partners becomes single-select (reads/writes `values.rider`)
 *   showDateRange   — adds From/To pickers. Year/Month and an explicit range are
 *                     two ways of saying the same thing, so picking one clears
 *                     and disables the other rather than silently combining them
 */
export default function DashboardFilterBar({
  values,
  onChange,
  onRefresh,
  showYearMonth = true,
  showPartners = false,
  yearMonthFirst = false,
  showAllOption = false,
  multiZone = false,
  allowAllPeriod = false,
  singlePartner = false,
  showDateRange = false,
}) {
  const { options: projectOptions, loading: projectsLoading } =
    useSettingsDropdown("projects");
  const { options: fleetOptions, loading: fleetsLoading } = useSettingsDropdown(
    "fleet-vehicle-types",
    { filter: (item) => item.category === "fleet" }
  );
  const { options: cityOptions, loading: citiesLoading } = useSettingsDropdown(
    "city-zones",
    { filter: (item) => item.entityType === "city" }
  );
  const {
    options: zoneOptions,
    data: zoneData,
    loading: zonesLoading,
  } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "zone",
  });

  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);

  const selectedCity =
    values.city && values.city !== ALL_VALUE ? values.city : null;

  // A zone belongs to exactly one city, so picking a city narrows the zone list.
  const visibleZoneOptions = useMemo(() => {
    if (!multiZone || !selectedCity) return zoneOptions;
    return zoneData
      .filter((zone) => String(zone.parentCity || "") === String(selectedCity))
      .map((zone) => ({ label: zone.name, value: zone._id }));
  }, [multiZone, selectedCity, zoneData, zoneOptions]);

  // Changing city must not leave zones selected that no longer belong to it.
  useEffect(() => {
    if (!multiZone || !values.zones?.length) return;
    const allowed = new Set(visibleZoneOptions.map((o) => o.value));
    const kept = values.zones.filter((id) => allowed.has(id));
    if (kept.length !== values.zones.length) {
      onChange({ ...values, zones: kept });
    }
  }, [multiZone, visibleZoneOptions, values, onChange]);

  // Partner options are scoped to the current Project / Fleet / City / Zone combo.
  const partnerScope = useMemo(
    () => ({
      project: values.project,
      fleetType: values.fleetType,
      city: values.city,
      zone: values.zone,
      zones: values.zones,
    }),
    [values.project, values.fleetType, values.city, values.zone, values.zones]
  );

  useEffect(() => {
    if (!showPartners) return;

    let cancelled = false;
    setPartnersLoading(true);

    fetchPartnerOptions(partnerScope)
      .then((opts) => {
        if (!cancelled) setPartnerOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setPartnerOptions([]);
      })
      .finally(() => {
        if (!cancelled) setPartnersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showPartners, partnerScope]);

  const set = (key, value) => {
    onChange({ ...values, [key]: value });
  };

  const ALL_OPTION = { label: "All", value: ALL_VALUE };
  const withAll = (options) =>
    showAllOption ? [ALL_OPTION, ...options] : options;
  const withAllPeriod = (options) =>
    allowAllPeriod ? [ALL_OPTION, ...options] : options;

  // Exclusivity is enforced by clearing, not disabling: the tab lands with a
  // Year already selected, so disabling the pickers would leave the date range
  // permanently unreachable unless the user first knew to set Year to "All".
  const clearRange = showDateRange ? { fromDate: null, toDate: null } : {};

  // A month with no year is not a date range, so clearing Year clears Month too.
  const setYear = (v) => {
    const clearsMonth = allowAllPeriod && (v === ALL_VALUE || v === undefined);
    onChange({
      ...values,
      year: v,
      ...(clearsMonth ? { month: ALL_VALUE } : {}),
      ...clearRange,
    });
  };

  const setMonth = (v) => onChange({ ...values, month: v, ...clearRange });

  const setRangeBound = (key, value) => {
    const iso = value ? value.format("YYYY-MM-DD") : null;
    const next = { ...values, [key]: iso };
    // Any custom range wins over Year/Month, so reset those to "All".
    if (iso && allowAllPeriod) {
      next.year = ALL_VALUE;
      next.month = ALL_VALUE;
    }
    onChange(next);
  };

  const periodDisabled =
    allowAllPeriod && (values.year === ALL_VALUE || values.year === undefined);

  const yearMonthFields = showYearMonth ? (
    <>
      <div className="dash-filter-field">
        <span className="dash-filter-label">Year</span>
        <Select
          allowClear={!allowAllPeriod}
          placeholder="Year"
          options={withAllPeriod(YEAR_OPTIONS)}
          value={values.year}
          onChange={setYear}
          className="dash-filter-select"
        />
      </div>
      <div className="dash-filter-field">
        <span className="dash-filter-label">Month</span>
        <Select
          allowClear={!allowAllPeriod}
          placeholder="Month"
          options={withAllPeriod(MONTH_OPTIONS)}
          value={values.month}
          onChange={setMonth}
          className="dash-filter-select"
          disabled={periodDisabled}
          title={periodDisabled ? "Select a year first" : undefined}
        />
      </div>
    </>
  ) : null;

  const dateRangeFields = showDateRange ? (
    <>
      <div className="dash-filter-field">
        <span className="dash-filter-label">From Date</span>
        <DatePicker
          className="dash-filter-select"
          format="DD/MM/YYYY"
          value={values.fromDate ? dayjs(values.fromDate) : null}
          onChange={(d) => setRangeBound("fromDate", d)}
          maxDate={values.toDate ? dayjs(values.toDate) : undefined}
        />
      </div>
      <div className="dash-filter-field">
        <span className="dash-filter-label">To Date</span>
        <DatePicker
          className="dash-filter-select"
          format="DD/MM/YYYY"
          value={values.toDate ? dayjs(values.toDate) : null}
          onChange={(d) => setRangeBound("toDate", d)}
          minDate={values.fromDate ? dayjs(values.fromDate) : undefined}
        />
      </div>
    </>
  ) : null;

  const coreFields = (
    <>
      <div className="dash-filter-field">
        <span className="dash-filter-label">Project</span>
        <Select
          allowClear={!showAllOption}
          placeholder="Project"
          options={withAll(projectOptions)}
          loading={projectsLoading}
          value={values.project}
          onChange={(v) => set("project", v)}
          className="dash-filter-select"
          showSearch
          optionFilterProp="label"
        />
      </div>
      <div className="dash-filter-field">
        <span className="dash-filter-label">Fleet Type</span>
        <Select
          allowClear={!showAllOption}
          placeholder="All"
          options={withAll(fleetOptions)}
          loading={fleetsLoading}
          value={values.fleetType}
          onChange={(v) => set("fleetType", v)}
          className="dash-filter-select"
        />
      </div>
      <div className="dash-filter-field">
        <span className="dash-filter-label">City</span>
        <Select
          allowClear={!showAllOption}
          placeholder="All"
          options={withAll(cityOptions)}
          loading={citiesLoading}
          value={values.city}
          onChange={(v) => set("city", v)}
          className="dash-filter-select"
          showSearch
          optionFilterProp="label"
        />
      </div>
      <div className="dash-filter-field">
        <span className="dash-filter-label">Zones</span>
        {multiZone ? (
          <Select
            mode="multiple"
            maxTagCount={1}
            allowClear
            placeholder="All Zones"
            options={visibleZoneOptions}
            loading={zonesLoading}
            value={values.zones || []}
            onChange={(v) => set("zones", v)}
            className="dash-filter-select dash-filter-select-wide"
            showSearch
            optionFilterProp="label"
            notFoundContent={
              zonesLoading
                ? "Loading…"
                : selectedCity
                  ? "No zones in this city"
                  : "No zones"
            }
          />
        ) : (
          <Select
            allowClear
            placeholder="Select Zones"
            options={zoneOptions}
            loading={zonesLoading}
            value={values.zone}
            onChange={(v) => set("zone", v)}
            className="dash-filter-select"
            showSearch
            optionFilterProp="label"
          />
        )}
      </div>
    </>
  );

  return (
    <div className="dash-filter-bar">
      {yearMonthFirst ? yearMonthFields : null}
      {yearMonthFirst ? dateRangeFields : null}
      {coreFields}
      {!yearMonthFirst ? yearMonthFields : null}
      {!yearMonthFirst ? dateRangeFields : null}

      {showPartners && (
        <div className="dash-filter-field">
          <span className="dash-filter-label">Partners</span>
          {singlePartner ? (
            <Select
              allowClear={!showAllOption}
              placeholder="All Partners"
              options={withAll(partnerOptions)}
              loading={partnersLoading}
              value={values.rider}
              onChange={(v) => set("rider", v)}
              className="dash-filter-select dash-filter-select-wide"
              showSearch
              optionFilterProp="label"
              notFoundContent={partnersLoading ? "Loading…" : "No partners"}
            />
          ) : (
            <Select
              allowClear
              mode="multiple"
              maxTagCount={1}
              placeholder="All Partners"
              options={partnerOptions}
              loading={partnersLoading}
              value={values.partners || []}
              onChange={(v) => set("partners", v)}
              className="dash-filter-select dash-filter-select-wide"
              showSearch
              optionFilterProp="label"
              notFoundContent={partnersLoading ? "Loading…" : "No partners"}
            />
          )}
        </div>
      )}

      <Button
        type="default"
        icon={<ReloadOutlined />}
        className="dash-filter-refresh"
        onClick={onRefresh}
        title="Reset filters & reload"
      />
    </div>
  );
}
